import * as DOMUtils from "history/DOMUtils";
import {
    find,
    objectToQueryString,
    parseSpecificationValue,
    propagateCommonQueryParamsAsString,
} from "../../../../common-deprecated/utils";
import CarDataConsolidator from "../../../shared-logic/utils/classes/CarDataConsolidator";
import type { EcoLabelPopupLabelType } from "../../../../common-deprecated/types/SettingsType";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { ItemsType, StateType } from "../../../../common-deprecated/redux/typeHelpers";
import {
    type AlternativeType,
    type FinanceOptionType,
    type PricePromotionType,
    type PriceType,
    type WinterWheelType,
    PromotionTypeEnum,
} from "../../../../common-deprecated/types/CommonTypes";
import type { ExternalConfigType } from "../../../shared-logic/types/ExternalPriceType";
import type { BasketType, MinimizedBasketType } from "../../../shared-logic/types/BasketType";
import type { FinanceRateStateType } from "../redux/reducers/FinanceRateReducer";
import {
    type CertificationType,
    type WinterWheelCategoryType,
    CERTIFICATION_CORRELATED_NEDC,
    CERTIFICATION_NEDC,
    CERTIFICATION_WLTP,
    EFFICIENCY_CLASS,
    EFFICIENCY_CLASS_TMEF,
} from "../../../../common-deprecated/constants";
import type { ModelFilterStateType } from "../redux/reducers/ModelFilterReducer";
import type { BuildAndBuyStateType } from "../redux/store";
import type { MassManagementStateType } from "../redux/reducers/MassManagementReducer";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import { showBnbMonthlyRates } from "./settings/bnbFinanceSettingUtils";
import type { BnbSettingsType } from "../redux/reducers/BnbSettingsReducer";
import { getModelMapValueById } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import { EquipmentAvailabilityEnum } from "../../../../common-deprecated/server/texusEquipmentTypes";
import { getBnbLabel, getBnbOptionalLabel } from "./labels";
import Navigation, { NavStep } from "../../shared-logic/utils/Navigation";
import type { TradeInAemExternalDataReducerType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import type { GroupedSpecType } from "./pdfUtils";
import { getSelectedFinancePackEquipment } from "../../../shared-logic/utils/packUtils";
import { addPrices } from "../../../shared-logic/utils/priceUtils";
import type {
    ExternalPriceCalulatedPriceType,
    ExternalPriceStateType,
} from "../../../shared-logic/types/externalPriceTypes";
import { minimizeBasket } from "../../../shared-logic/utils/basketUtils";
import { getExternalConfigOptionsAccessoryIdsDeprecated } from "../../../shared-logic/utils/typedUtils";
import type { PriceInfoTexusType } from "../../../../common-deprecated/types/TexusTypes";
import { extractAdditionalInfoFromExternalPrice } from "../../../shared-logic/utils/externalPriceUtils";
import type { FinancePackConfigurablesType } from "../../../shared-logic/types/FinanceTypes";
import { isAllowedFinanceProductForTradeIn } from "./settings/bnbSettingUtils";
import type { TyreInfoLabels } from "../../../../shared-logic/utils/tyreInfoContants";

/**
 * Method which adds the finance pack logic to the configuration object
 * Put this into a separate method to separate everything regarding finance packs
 */
const getExternalConfigurationFinancePack = (
    basket: BasketType,
    items: ItemsType,
    financeOption: FinanceOptionType | null = null,
): {
    financePackServiceProducts: ExternalConfigType["ServiceProducts"];
    toBeRemovedServiceProducts: ExternalConfigType["ServiceProducts"];
    selectedFinancePacks: ExternalConfigType["Packs"];
} => {
    const { financePacks, financePackServiceProducts, serviceProducts } = basket;
    let selectedFinancePacks: ExternalConfigType["Packs"] = [];
    const parsedServiceProducts: ExternalConfigType["ServiceProducts"] = [];
    let toBeRemovedServiceProducts: ExternalConfigType["ServiceProducts"] = [];

    if (financeOption === "monthly") {
        // Remove service products which are part of a selected finance pack (only applicable in monthly)
        const financePacksServiceProducts = financePacks.reduce((packServiceProduct, pack) => {
            return [...packServiceProduct, ...pack.serviceProducts];
        }, [] as FinancePackConfigurablesType[]);

        toBeRemovedServiceProducts = serviceProducts
            .filter((serviceProduct) => find(serviceProduct.id, financePacksServiceProducts))
            .map((serviceProduct) => serviceProduct.id);

        // Add the service products from within the pack
        if (financePackServiceProducts.length > 0) {
            parsedServiceProducts.push(...financePackServiceProducts.map((serviceProduct) => serviceProduct.id));
        }
    }

    if (financeOption === "monthly" && financePacks.length > 0) {
        selectedFinancePacks = financePacks.map((pack) => pack.id);
    }

    return {
        financePackServiceProducts: parsedServiceProducts.length ? parsedServiceProducts : undefined,
        toBeRemovedServiceProducts: toBeRemovedServiceProducts?.length ? toBeRemovedServiceProducts : undefined,
        selectedFinancePacks: selectedFinancePacks.length ? selectedFinancePacks : undefined,
    };
};

/**
 * Method which adds specific Pararamters when a finance pack is selected split up for readability
 * Put this into a separate method to separate everything regarding finance packs
 */
const getExternalConfigurationFinancePackParameters = (
    basket: BasketType,
    items: ItemsType,
    financeOption: FinanceOptionType | null = null,
): Record<string, string[]> | null => {
    // Following extra info is only relevant when finance option is cash and finance packs are not part of the payload
    // Since customer portal can't know what the optional packs are and which equipment belongs in there, we add this to the Parameters part of ccSave
    if (financeOption === "cash" && items.financePacks.length > 0) {
        return items.financePacks.reduce(
            (acc, pack) => {
                const financePackContent = getSelectedFinancePackEquipment(pack, basket, items, financeOption);
                if (financePackContent.standard.length > 0 || financePackContent.optional.length > 0) {
                    if (!acc) acc = {};
                    acc[pack.id] = [...financePackContent.standard, ...financePackContent.optional].map(
                        (fpc) => fpc.id,
                    );
                }
                return acc;
            },
            null as Record<string, string[]> | null,
        );
    }
    return null;
};

/**
 * Method which forms the base of all the external configuration objects used throughout the application
 */
const getExternalConfigurationBaseObject = (
    settings: CommonSettingsType,
    car: CarType,
    basket: BasketType,
    items: ItemsType,
    // TODO Remove excludeNonFinanceableServicePlans parameter and clean up
    excludeNonFinanceableServicePlans?: boolean,
    // TODO Remove passing of financeOption
    financeOption: FinanceOptionType | null = null,
    // TODO Remove and clean up
    addPromotionInfo: boolean = false,
    extraCosts?: ExternalPriceCalulatedPriceType["extraCosts"],
    financeProductType?: string,
): ExternalConfigType => {
    const { packs, serviceProducts } = basket;
    const externalConfig: ExternalConfigType = {
        Country: settings.country,
        Brand: settings.brand,
        Language: settings.language,
        ModelID: car.model.id,
        CarID: car.id,
        ExteriorColourID: basket.colour.id,
        UpholsteryID: basket.upholstery.id,
    };

    if (packs.length > 0) externalConfig.Packs = packs.map((pack: any) => pack.id);

    // Only exclude the non financeable serviceProducts on the response to TFS
    const serviceProductsArray = excludeNonFinanceableServicePlans
        ? serviceProducts.filter((serviceProduct) => serviceProduct.financeable)
        : serviceProducts;

    if (serviceProductsArray.length) {
        externalConfig.ServiceProducts = serviceProductsArray.map((serviceProduct) => serviceProduct.id);
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // This section was put separately in the attempt to make all logic concerning finance packs more readable. //
    const { financePackServiceProducts, toBeRemovedServiceProducts, selectedFinancePacks } =
        getExternalConfigurationFinancePack(basket, items, financeOption);
    if (financePackServiceProducts) {
        if (!externalConfig.ServiceProducts) {
            externalConfig.ServiceProducts = financePackServiceProducts;
        } else {
            externalConfig.ServiceProducts = externalConfig.ServiceProducts.concat(financePackServiceProducts);
        }
    }

    // Remove service products which are part of a finance pack when monthly is selected
    if (toBeRemovedServiceProducts && externalConfig.ServiceProducts) {
        externalConfig.ServiceProducts = externalConfig.ServiceProducts.reduce((acc: string[], sp) => {
            if (!toBeRemovedServiceProducts.find((ele) => ele === sp)) {
                acc.push(sp);
            }
            return acc;
        }, []);
    }

    if (selectedFinancePacks) {
        if (!externalConfig.Packs) {
            externalConfig.Packs = selectedFinancePacks;
        } else {
            externalConfig.Packs = externalConfig.Packs.concat(selectedFinancePacks);
        }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////

    const { options, accessories } = getExternalConfigOptionsAccessoryIdsDeprecated(
        basket,
        items,
        false,
        false,
        financeOption,
    );

    if (basket.winterWheel && !basket.winterWheel.installTheWheels) {
        externalConfig.ExcludeMountingCosts = [basket.winterWheel.id];
    }

    if (options.length > 0) externalConfig.Options = options;
    if (accessories.length > 0) externalConfig.Accessories = accessories;

    if (addPromotionInfo && basket.total) {
        const { discount, promotions, onlineMonthlyDiscount } = basket.total;
        const totalNonFinanceable = serviceProducts
            .filter((serviceProduct) => !serviceProduct.financeable)
            .reduce((initialValue, servicePlan) => initialValue + servicePlan.price.cash, 0);
        const ecoBonusPromotionsPrice = promotions
            .filter((promotion) => promotion.ecoBonus)
            .reduce((initialValue, promotion) => initialValue + promotion.amount, 0);

        if (promotions && promotions.length > 0) {
            externalConfig.Promotions = promotions.map((promo) => promo.id);
        }

        // Remove the price of the non financeable serviceProducts from the total price so it won't affect the monthly rates
        // Add the price of the ecobonus promotions as these are not meant to be reducted when sending to the finance service
        externalConfig.TotalPrice =
            discount + ecoBonusPromotionsPrice - (excludeNonFinanceableServicePlans ? totalNonFinanceable : 0);
        // Dynamically add it to the ExternalConfig object, considering as soon as it's defined it will return its value in the ccGetMonthlyPayment
        if (discount !== onlineMonthlyDiscount) {
            externalConfig.TotalPriceInclPromotions = onlineMonthlyDiscount;
        }
    }

    if (extraCosts) {
        if (!externalConfig.Costs) externalConfig.Costs = [];
        // TODO: what with Value? Needs to be alligned further as there can normally be multiple values
        externalConfig.Costs = extraCosts.map((cost) => ({
            Name: cost.key,
            TaxDefCode: typeof cost.eposVatCode !== "undefined" ? String(cost.eposVatCode) : undefined,
            Code: cost.eposCode || "",
            Amount: cost.values[0]?.amount,
            Selected: cost?.eposSelectionIndicator === "YES",
        }));
    }

    if (financeProductType) {
        externalConfig.ProductType = financeProductType;
    }

    return externalConfig;
};

/**
 * This method needs to be split up more when moving away from the getExternalConfigurationObject
 */
const getParametersForExternalConfigurationObject = (
    basket: BasketType,
    items: ItemsType,
    settings: CommonSettingsType,
    quotes: { finance: string | null; insurance: string | null; enabled: boolean } | null,
    financeOption: FinanceOptionType | null = null,
    co2Value: string = "",
    tradeInAemData: TradeInAemExternalDataReducerType,
    taxLocationId?: string | null,
    financeProductName?: string | null,
    disableTradeInForFinanceProducts?: string[] | undefined,
): ExternalConfigType["Parameters"] => {
    const parameters: ExternalConfigType["Parameters"] = { Origin: "build" };

    const financeParameters = getExternalConfigurationFinancePackParameters(basket, items, financeOption);
    if (financeParameters) {
        parameters.FinancePacks = financeParameters;
    }

    if (quotes && quotes.enabled && (quotes.finance || quotes.insurance)) {
        if (quotes.finance) parameters.FinanceQuoteID = quotes.finance;
        if (quotes.insurance) parameters.InsuranceQuoteID = quotes.insurance;
    }

    // Only add trade-in value if it's compatible with the current financeProduct.
    if (tradeInAemData.data.vehicleValue) {
        if (isAllowedFinanceProductForTradeIn(disableTradeInForFinanceProducts, financeProductName)) {
            parameters.TradeInValue = tradeInAemData.data.vehicleValue;
            parameters.TradeInQuoteID = tradeInAemData.data.quoteId;
        }
    }

    if (financeOption) {
        parameters.FinanceOption = financeOption;
    }

    if (co2Value) {
        parameters.Specifications = {
            Co2: Number(co2Value),
        };
    }

    if (taxLocationId) {
        parameters.UserDepartmentCode = taxLocationId;
    }

    if (settings.query.saveDealerId) {
        parameters.DealerId = settings.query.saveDealerId;
    }

    return parameters;
};

/**
 * Method used for the finance calculators and monthly rate fetches
 */
export const getFinanceExternalConfigurationObject = (
    settings: CommonSettingsType,
    car: CarType,
    basket: BasketType,
    items: ItemsType,
    tradeInAemData: TradeInAemExternalDataReducerType,
    quotes: { finance: string | null; insurance: string | null; enabled: boolean } | null,
    externalPrice: ExternalPriceStateType,
    financeProductType: string,
    financeProductName?: string | null,
    disableTradeInForFinanceProducts?: string[] | undefined,
): ExternalConfigType => {
    const externalConfig = getExternalConfigurationBaseObject(
        settings,
        car,
        basket,
        items,
        true,
        null,
        true,
        externalPrice.calculatedPrice.extraCosts,
        financeProductType,
    );
    const parameters = getParametersForExternalConfigurationObject(
        basket,
        items,
        settings,
        quotes,
        null,
        externalPrice.co2Specs.value,
        tradeInAemData,
        undefined,
        financeProductName,
        disableTradeInForFinanceProducts,
    );
    if (parameters) externalConfig.Parameters = parameters;

    const tmefAdditionalInfo = extractAdditionalInfoFromExternalPrice(externalPrice.calculatedPrice);
    if (tmefAdditionalInfo) {
        if (!externalConfig.Parameters) externalConfig.Parameters = {};
        if (!externalConfig.Parameters.Specifications) externalConfig.Parameters.Specifications = {};
        externalConfig.Parameters.Specifications.AdditionalInfo = tmefAdditionalInfo;
    }

    return externalConfig;
};

export const getCcSaveExternalConfigurationObject = (
    settings: CommonSettingsType,
    car: CarType,
    basket: BasketType,
    items: ItemsType,
    tradeInAemData: TradeInAemExternalDataReducerType,
    quotes: { finance: string | null; insurance: string | null; enabled: boolean } | null,
    addPromotionInfo: boolean = false,
    financeOption: FinanceOptionType | null = null,
    co2Value: string = "",
    financeProductType: string,
    excludeNonFinanceableServicePlans?: boolean,
    taxLocationId?: string | null,
    extraCosts?: ExternalPriceCalulatedPriceType["extraCosts"],
): ExternalConfigType => {
    const externalConfig = getExternalConfigurationBaseObject(
        settings,
        car,
        basket,
        items,
        excludeNonFinanceableServicePlans,
        financeOption,
        addPromotionInfo,
        extraCosts,
        financeProductType,
    );
    const parameters = getParametersForExternalConfigurationObject(
        basket,
        items,
        settings,
        quotes,
        financeOption,
        co2Value,
        tradeInAemData,
        taxLocationId,
    );

    if (parameters) externalConfig.Parameters = parameters;

    return externalConfig;
};

/**
 * @deprecated - This has been split into more specific utility methods to retrieve the configuration object.
 * The first method specifically for Finance/insurance calculators and fetching the monthly rate has already been created -> getFinanceInsuranceExternalConfigurationObject
 * Second method for ccSave has been created as well -> getCcSaveExternalConfigurationObject
 * TODO: The other method(s)? Still need to be made in a subsequent refactor (transactionLogic, insurance, etc,...) also the tests need to be split up decently
 */
export const getExternalConfigurationObject = (
    settings: CommonSettingsType,
    car: CarType,
    basket: BasketType,
    items: ItemsType,
    tradeInAemData: TradeInAemExternalDataReducerType,
    addPromotionInfo: boolean = false,
    quotes: { finance: string | null; insurance: string | null; enabled: boolean } | null,
    financeOption: FinanceOptionType | null = null,
    co2Value: string = "",
    excludeNonFinanceableServicePlans?: boolean,
    taxLocationId?: string | null,
    extraCosts?: ExternalPriceCalulatedPriceType["extraCosts"],
): ExternalConfigType => {
    const externalConfig = getExternalConfigurationBaseObject(
        settings,
        car,
        basket,
        items,
        excludeNonFinanceableServicePlans,
        financeOption,
        addPromotionInfo,
        extraCosts,
    );
    const parameters = getParametersForExternalConfigurationObject(
        basket,
        items,
        settings,
        quotes,
        financeOption,
        co2Value,
        tradeInAemData,
        taxLocationId,
    );
    if (parameters) externalConfig.Parameters = parameters;

    return externalConfig;
};

export const minimizeBuildBasket = (
    basket: BasketType,
    car: CarType,
    settings: CommonSettingsType,
): MinimizedBasketType => {
    const baseBasket = minimizeBasket(basket, car);

    const selectedEquipment = settings.query.selectedEquipment
        ? settings.query.selectedEquipment.split(",").map(Number)
        : null;

    // Finance packs
    baseBasket.fp = basket.financePacks.map((financePack) => financePack.shortId);
    // Finance pack extras
    baseBasket.fpe = basket.financePackExtras.map((financePackExtra) => financePackExtra.shortId);
    // Finance pack service products
    baseBasket.fpsp = basket.financePackServiceProducts.map(
        (financePackServiceProduct) => financePackServiceProduct.shortId,
    );
    // Exclude the selectedEquipment since else they will cause duplicate entries in the querystring and stack :o
    baseBasket.e = (
        selectedEquipment
            ? basket.extras.filter((extra) => !selectedEquipment.find((se) => se === parseInt(extra.shortId)))
            : basket.extras
    )
        .filter((extra) => !extra.isCharger)
        .map((extra) => extra.shortId);
    baseBasket.ec = basket.extras.reduce((ec: string[], extra) => {
        const extraChildOption = CarDataConsolidator.getSelectedExtraOption(extra);
        if (extraChildOption) ec.push(extraChildOption.shortId);
        return ec;
    }, []);
    if (basket.primaryAccentColour) baseBasket.pac = basket.primaryAccentColour.id;
    if (basket.secondaryAccentColour) baseBasket.sac = basket.secondaryAccentColour.id;

    return baseBasket;
};

/**
 * Function which checks if the monthly rate should be hidden when receiving a certain ccGetMonthlyPayment response
 */
export const pslsFinanceResponseHasNoQuotes = (
    finance: FinanceRateStateType,
    bnbSettings: BnbSettingsType,
): boolean => {
    return Object.keys(finance.quoteInfo).length === 0 && bnbSettings.addPSLSToFinanceConfigurationObject;
};

export type ModelFilterSlideType = { type: string; mode: string; frame: number }[];
export const getModelFilterSlideTypes = (modelFilter: ModelFilterStateType): ModelFilterSlideType => {
    const slideTypes = modelFilter.filters.reduce((sliderTypes: ModelFilterSlideType, filter) => {
        const slideType = getModelFilterSilderImageCode(filter.code);

        if (!sliderTypes.find((sliderType) => sliderType.type === slideType)) {
            switch (slideType) {
                case "TYPE":
                    sliderTypes.push({ type: slideType, mode: "day-exterior", frame: 9 });
                    break;
                case "CAP":
                    sliderTypes.push({ type: slideType, mode: "top-view-interior", frame: 0 });
                    break;
            }
        }
        return sliderTypes;
    }, []);

    // If by any chance no TYPE slider is activated, make sure to default to showing one image.
    if (slideTypes.length === 0) {
        slideTypes.push({
            type: getModelFilterSilderImageCode(modelFilter.filters[0].code),
            mode: "day-exterior",
            frame: 9,
        });
    }

    return slideTypes;
};

export const getModelFilterSilderImageCode = (code: string): string => {
    // Slide images are based on the first word of the slider type.
    return code.split("_")[0];
};

export const getCarDescription = (car: StateType["car"], isSummary: boolean = false): string => {
    const { model, body, grade, motorization } = car;
    if (isSummary) {
        const webtitleSectionNames = [model.webtitleSectionName, grade.webtitleSectionName, body.webtitleSectionName]
            .filter(Boolean)
            .join(", ");
        return `${webtitleSectionNames}<br/>${motorization.engine.name}<br/>${motorization.transmission.name} (${motorization.wheeldrive.name}) `;
    } else {
        return `${model.webtitleSectionName} ${grade.webtitleSectionName} ${body.webtitleSectionName} ${motorization.engine.name} ${motorization.transmission.name} (${motorization.wheeldrive.name}) `;
    }
};

export const GetColourForEULabel = (value: string): string => {
    switch (value.toUpperCase()) {
        case "A":
            return "#33a357";
        case "B":
            return "#79b752";
        case "C":
            return "#c3d545";
        case "D":
            return "#fff12c";
        case "E":
            return "#edb731";
        case "F":
            return "#d66f2c";
        case "G":
            return "#cc232a";
        default:
            return "#020202";
    }
};

export const getCorrectWinterWheelCategoryLabel = (
    wheelCategory: WinterWheelCategoryType,
):
    | "winterWheelsModalSoftCompoundEuropean"
    | "winterWheelsModalSoftCompoundNordic"
    | "winterWheelsModalStudded"
    | "winterWheelsModalSoftCompound" => {
    switch (wheelCategory) {
        case "SOFT_COMPOUND_EU": {
            return "winterWheelsModalSoftCompoundEuropean";
        }
        case "SOFT_COMPOUND_NORDIC": {
            return "winterWheelsModalSoftCompoundNordic";
        }
        case "STUDDED": {
            return "winterWheelsModalStudded";
        }
        default: {
            return "winterWheelsModalSoftCompound";
        }
    }
};

export const calculateWinterWheelPrice = (winterWheel: WinterWheelType): PriceType => {
    let { price } = winterWheel;

    if (winterWheel.installTheWheels && winterWheel.installationCost) {
        price = addPrices([price, winterWheel.installationCost]);
    }
    return price;
};

// Method which categorizes specs based on their certification property
type SpecBaseType = { certification: CertificationType };
type CategoryReducedSpecType<T extends SpecBaseType> = { title: string; specs: T[] };
type CategoryReduceType<T extends SpecBaseType> = {
    nedc: CategoryReducedSpecType<T>;
    correlatedNedc: CategoryReducedSpecType<T>;
    wltp: CategoryReducedSpecType<T>;
    none: CategoryReducedSpecType<T>;
};
export const categorizeSpecs = <T extends SpecBaseType>(
    specs: T[],
    commonSettings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
): CategoryReduceType<T> => {
    const nedcLabel = getBnbOptionalLabel({ commonSettings, bnbSettings }, "nedcSpecificationTitle") || "";
    const correlatedNedcLabel =
        getBnbOptionalLabel({ commonSettings, bnbSettings }, "correlatedNedcSpecificationTitle") || "";
    const wltpLabel = getBnbOptionalLabel({ commonSettings, bnbSettings }, "wltpSpecificationTitle") || "";
    return specs.reduce(
        (acc, item) => {
            if (item.certification === CERTIFICATION_NEDC && nedcLabel) {
                acc.nedc.specs.push(item);
            } else if (item.certification === CERTIFICATION_CORRELATED_NEDC && correlatedNedcLabel) {
                acc.correlatedNedc.specs.push(item);
            } else if (item.certification === CERTIFICATION_WLTP && wltpLabel) {
                acc.wltp.specs.push(item);
            } else {
                acc.none.specs.push(item);
            }
            return acc;
        },
        {
            // the order of these entries will decided in which order they are rendered
            nedc: { title: nedcLabel, specs: [] },
            correlatedNedc: { title: correlatedNedcLabel, specs: [] },
            wltp: { title: wltpLabel, specs: [] },
            none: { title: "", specs: [] },
        } as CategoryReduceType<T>,
    );
};

// This is a build specific setting, cannot be defined in Settings as it requires a helper and that would cause dependency issues
export const showMonthlyRatesInBasket = (
    commonSettings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
    finance: FinanceRateStateType,
    step: NavStep,
    modelCode?: string,
): boolean =>
    showBnbMonthlyRates(commonSettings, bnbSettings, modelCode, step) &&
    (!pslsFinanceResponseHasNoQuotes(finance, bnbSettings) || finance.fetching);

// Utility method which returns the correct car price as required if the external price calculation is active or not
export const getCarPrice = (basket: BasketType, car: CarType, externalPrice: ExternalPriceStateType): PriceType => {
    // If total price isExternalPrice is set to true, we can be sure that the calculatedPrice.carPrice is filled in.
    return basket.total.isExternalPrice && !externalPrice.error && !externalPrice.hadError
        ? externalPrice.calculatedPrice.carPrice!
        : car.price;
};

// Only the ones relevant for the utility method are defined. Texus returns more properties than these.
// No support yet for:
// ExteriorColourType & UpholsteryType (not returned from Texus on the promo.DefaultCarForPromotion Object).
// ExteriorColour, Upholstery, Options, Accessories and Packs due to the fact that promo.DefaultCarForPromotion is null in FetchCar when doing a
// GetFullConfiguration call. It's only filled in when doing a getCarModel call.
export enum TexusPromotionApplicability {
    Model = "MODELGENERATION",
    SubModel = "SUBMODEL",
    Body = "BODY",
    Engine = "ENGINE",
    Transmission = "TRANSMISSION",
    Wheeldrive = "WHEELDRIVE",
    Grade = "GRADE",
    Car = "CAR",
}

export type TexusPromotionDefaultConfigurationType = {
    CarID: string;
    ModelID: string;
    SubModelID: string;
    BodyTypeID: string;
    EngineID: string;
    GradeID: string;
    TransmissionID: string;
    WheelDriveID: string;
    WheelID: string;
    UpholsteryID: string;
    ExteriorColourID: string;
};
// Constant which maps the promotiontype to the actual configuration object property
const promotionTypeMapping: Record<TexusPromotionApplicability, keyof TexusPromotionDefaultConfigurationType> = {
    [TexusPromotionApplicability.Model]: "ModelID",
    [TexusPromotionApplicability.SubModel]: "SubModelID",
    [TexusPromotionApplicability.Body]: "BodyTypeID",
    [TexusPromotionApplicability.Engine]: "EngineID",
    [TexusPromotionApplicability.Transmission]: "TransmissionID",
    [TexusPromotionApplicability.Wheeldrive]: "WheelDriveID",
    [TexusPromotionApplicability.Grade]: "GradeID",
    [TexusPromotionApplicability.Car]: "CarID",
};
type PromoTargetType = { id?: string; target: TexusPromotionApplicability }[];
type TexusSpotlightBaseType = {
    PromotionType: PromotionTypeEnum;
    Target: TexusPromotionApplicability;
    ID: string;
    DefaultCarForPromotion: TexusPromotionDefaultConfigurationType | null;
};
// Method which returns only the spotlight promotions
export const getTexusSpotLightPromotions = <T extends TexusSpotlightBaseType>(
    arr: T[],
    promoTargets: PromoTargetType,
): T[] => {
    // Texus actually returns PromotionType camel cased, therefor we do the toUpperCase()
    return arr.filter((item) => {
        const validatesForTarget = promoTargets.reduce((acc: boolean, promoTarget) => {
            if (acc) return acc;
            let validatesForId = true;
            if (promoTarget.id) {
                // If the DefaultCarForPromotion is not present but it is required to be checked, it should never validate
                if (!item.DefaultCarForPromotion) return false;
                const configurationId = item.DefaultCarForPromotion[promotionTypeMapping[promoTarget.target]];
                validatesForId = configurationId === promoTarget.id;
            }
            return item.Target === promoTarget.target && validatesForId;
        }, false);
        return item.PromotionType.toUpperCase() === PromotionTypeEnum.SpotLight && validatesForTarget;
    });
};

export const generateImageSet = (
    getFrame: (width: number, height: number) => string,
    width: number,
    height: number,
): { src: string; srcset: string } => {
    const frame1 = getFrame(width, height);
    const frame2 = getFrame(width * 2, height * 2);
    const frame3 = getFrame(width * 3, height * 3);
    return {
        src: frame1,
        srcset: `${frame1} 1x, ${frame2} 2x, ${frame3} 3x`,
    };
};

/**
 * Method which checks if the mass validation is now ok or not
 */
export const isMassValidationOk = (massManagement: MassManagementStateType): boolean => {
    return massManagement.exceedingKilogram === 0;
};

export const countBasketItems = (state: BuildAndBuyStateType): number => {
    const { basket, car } = state;
    if (!basket.initialized || !car) return 0;
    return (
        Number(basket.colour.id !== car.colour.id) +
        Number(basket.wheel.id !== car.wheel.id) +
        Number(basket.upholstery.id !== car.upholstery.id) +
        basket.packs.length +
        basket.extras.length
    );
};

export const getEtaAlternativeUrl = (settings: CommonSettingsType, car: AlternativeType): string =>
    Navigation.build(
        settings,
        NavStep.Summary,
        { modelId: car.model.id, bodyTypeId: car.body.id, gradeId: car.grade.id, carId: car.id },
        objectToQueryString({
            c: car.colour.id,
            u: car.upholstery.id,
        }),
    );

export const hasWinterWheelInBasket = (basket: BasketType): boolean =>
    !!basket.winterWheel &&
    (basket.winterWheel.visible || basket.winterWheel.setItems.filter((setItem) => setItem.showsOnCar).length > 0);

/**
 * Filters out spotlight promotions and joins descriptions together
 */
export const combineSpotlightPromoDescription = (promotions: PricePromotionType[]): string => {
    // Check if there are spotlight promotions applicable, if so check if their description field is filled in and concatenate it
    return promotions
        .filter((promo) => promo.type === PromotionTypeEnum.SpotLight && !!promo.description)
        .map((promo) => promo.description)
        .join("<br/>");
};

/**
 * Used on most build steps as the "back" button in the secondary navigation.
 * Normally this goes to the grade overview page, but on standalone it redirects to one of the filters pages instead
 */
export const getGradeOverviewUrl = (settings: CommonSettingsType, modelId: string): string | undefined => {
    if (settings.isStandalone) {
        let url = `/${settings.country}/${settings.language}/model-filter`;

        const commonQueryParams = propagateCommonQueryParamsAsString(settings);
        let seperator = "?";
        if (commonQueryParams) {
            seperator = "&";
            url += `?${commonQueryParams}`;
        }
        if (settings.query.hideprices) {
            url += `${seperator}hideprices=true`;
        }
        return url;
    }
    const modelMap = getModelMapValueById(settings, modelId);
    return modelMap?.links?.carGrade && !modelMap.links.carGrade.includes("undefined")
        ? modelMap.links.carGrade
        : modelMap?.links?.modelPage;
};

export const hasEquipmentFilter = (state: BuildAndBuyStateType): boolean =>
    !!state.bnbSettings.preselectedEquipmentFilterId && !!state.model.filterItems.length;

export const hideWheelPriceOnPersonalization = (state: BuildAndBuyStateType): boolean =>
    state.bnbSettings.disableBuildAccessories && state.utils.activePersonalization === "wheel";

type EquipmentBaseType = {
    price: {
        discount: number;
        cash: number;
    };
    availability: {
        id: string;
        availability: EquipmentAvailabilityEnum;
        priceInfo: PriceInfoTexusType | null;
    }[];
};

/**
 * function to filter By Equipment Availability and adding equipment price to total price if the equipment is optional
 */
export const parseByEquipmentAvailability = <T extends EquipmentBaseType>(
    array: T[],
    isSelected: boolean,
    equipmentId: string,
): T[] => {
    if (isSelected) {
        return array
            .filter(
                (equipmentItem) =>
                    equipmentItem.availability.find((availability) => availability.id === equipmentId)?.availability !==
                    EquipmentAvailabilityEnum.NotAvailable,
            )
            .map((equipmentItem) => {
                const equipmentAvailability = equipmentItem.availability.find(
                    (availability) => availability.id === equipmentId,
                );
                // When the equipment availability is optional, add the equipment price to the total price.
                if (
                    equipmentAvailability?.availability === EquipmentAvailabilityEnum.Optional &&
                    equipmentAvailability?.priceInfo !== null
                ) {
                    // The price is added to the cash and the discount otherwise it would be seen as a discounted price.
                    return {
                        ...equipmentItem,
                        price: {
                            ...equipmentItem.price,
                            cash: equipmentItem.price.cash + equipmentAvailability.priceInfo.ListPriceWithDiscount,
                            discount:
                                equipmentItem.price.discount + equipmentAvailability.priceInfo.ListPriceWithDiscount,
                        },
                    };
                } else {
                    return equipmentItem;
                }
            });
    } else {
        return array.filter(
            (equipmentItem) =>
                equipmentItem.availability.find((availability) => availability.id === equipmentId)?.availability !==
                EquipmentAvailabilityEnum.Standard,
        );
    }
};

/**
 * Retrieve labels used for the shared tyre label functionality.
 */
export const tyreInfoBnbLabelSelector = (state: BuildAndBuyStateType): TyreInfoLabels => ({
    tyreInfo: getBnbLabel(state, "tyreInfo"),
    tyreInfoNoEprelFallback: getBnbLabel(state, "tyreInfoNoEprelFallback"),
    tyreInfoDescription: getBnbOptionalLabel(state, "tyreInfoDescription"),
    tyreInfoFallbackDescription: getBnbLabel(state, "tyreInfoFallbackDescription"),
    tyreInfoFallback: getBnbLabel(state, "tyreInfoFallback"),
});

/**
 * The grade nav shouldn't be configured on the build page anymore, but we still keep track of it for compatibility reasons
 */
export const getAemGradesNavHeight = (): number => {
    if (!DOMUtils.canUseDOM) return 0;
    const gradesNav = document.getElementById("cmp-gradesnavigation");
    return gradesNav?.clientHeight || 0;
};

/**
 * Returns the emission label when the current spec is an energy efficiency class, the ecoLabelpopup is available in the settings and the CO2 Combined WLTP value is available in the specs
 */
export const getEmissionLabel = (
    currentSpec: GroupedSpecType,
    allSpecs: GroupedSpecType[],
    commonSettings: CommonSettingsType,
): EcoLabelPopupLabelType | null => {
    // EFFICIENCY_CLASS was added here to make it possible to test on TZ
    if (
        ![EFFICIENCY_CLASS, EFFICIENCY_CLASS_TMEF].includes(currentSpec.internalCode) ||
        !commonSettings.ecoLabelpopup
    ) {
        return null;
    }

    const co2CombinedWltpSpec = allSpecs.find(
        ({ internalCode }) => internalCode === "CO2COMBINEDWLTP" || internalCode === "CO2COMBINEDCSWLTP",
    );
    if (!co2CombinedWltpSpec) return null;

    return {
        letter: currentSpec.value,
        label: parseSpecificationValue(co2CombinedWltpSpec, commonSettings.culture),
    };
};

export const getModelIdSelector = (state: BuildAndBuyStateType): string | undefined =>
    state.car?.model?.id || state.grade?.model?.id || state.model?.id || state.grades?.modelId;
